import type { MouseEventHandler } from 'react'
import { useState } from 'react'

import { formatDuration } from '@common/utils/time'

import { Tooltip } from '@/components/ui/tooltip'
import { openDialog } from '@/models/dialogs'
import { useAudioFile } from '@/modules/chatting/hooks/useAudioFile'
import DeleteIcon from '@/modules/chatting/icons/delete.svg'
import EditIcon from '@/modules/chatting/icons/edit.svg'
import type { Audio } from '@/modules/chatting/types'
import { AudioPlayPauseButton } from '@/modules/chatting/ui/components/AudioPlayPauseButton'
import { IconButton } from '@/modules/chatting/ui/components/icon-button'
import { classNames } from '@/utils/helpers'

import { DIALOG_TYPE } from '../constants'

interface Props {
  getAudioFile: (audio: Audio) => Promise<Blob>
  audio: Audio
  onClick: () => void
  isSelected: boolean
}

export const AudioListItem = ({
  audio,
  onClick,
  isSelected,
  getAudioFile,
}: Props) => {
  const { name, duration, description } = audio
  const [audioUrl, setAudioUrl] = useState<string>()
  const [isAudioDownloading, setIsAudioDownloading] = useState(false)
  const { handlePause, handlePlay, isPlaying } = useAudioFile(audioUrl)

  const onClickPlay = () => {
    if (!audioUrl) {
      setIsAudioDownloading(true)
      getAudioFile(audio)
        .then((file) => {
          setAudioUrl(URL.createObjectURL(file))
          setTimeout(() => {
            handlePlay()
            setIsAudioDownloading(false)
          }, 500)
        })
        .catch(() => {
          setIsAudioDownloading(false)
        })
    } else {
      handlePlay()
    }
  }

  const handleClickAction =
    (handler: () => void): MouseEventHandler<SVGElement> =>
    (event) => {
      event.stopPropagation()
      handler()
    }

  const onEdit = () => {
    openDialog({
      type: DIALOG_TYPE.EDIT_AUDIO,
      audio,
    })
  }

  const onDelete = () => {
    openDialog({
      type: DIALOG_TYPE.DELETE_AUDIO,
      audio,
    })
  }

  const content = (
    <>
      <div className="flex items-center gap-2 text-title">
        <AudioPlayPauseButton
          onPlay={handleClickAction(onClickPlay)}
          onPause={handleClickAction(handlePause)}
          isAudioDownloading={isAudioDownloading}
          isPlaying={isPlaying}
        />
        <div className="text-left font-medium">
          <div className="text-base leading-6">{name}</div>
          <div className="text-xs leading-5 text-secondary-300">
            {formatDuration(duration)}
          </div>
        </div>
      </div>
      <div className="flex items-center space-x-2">
        <IconButton
          icon={EditIcon}
          tooltip="Edit"
          onClick={handleClickAction(onEdit)}
          className="text-primary-500 hover:text-primary-600"
        />
        <IconButton
          icon={DeleteIcon}
          tooltip="Delete"
          onClick={handleClickAction(onDelete)}
          className="text-danger-default disabled:text-secondary-300 hover:text-danger-dark"
        />
      </div>
    </>
  )

  return (
    <div
      className={classNames(
        'cursor-pointer rounded-md border border-border-secondary',
        {
          'border-primary-500 bg-primary-50 dark:border-primary-500 dark:bg-primary-900':
            isSelected,
        },
      )}
    >
      {description ? (
        <Tooltip
          content={description}
          contentClassName="min-w-max"
          className="flex w-full items-center justify-between px-3 py-2"
          onClick={onClick}
        >
          {content}
        </Tooltip>
      ) : (
        <div
          className="flex w-full items-center justify-between px-3 py-2"
          onClick={onClick}
        >
          {content}
        </div>
      )}
    </div>
  )
}
